import { dungApiThat, goiApi } from '../api/http';
import { mayChuGoiNapToken } from '../../mock/goi-nap-token';
import { mayChuHanMucToken } from '../../mock/han-muc-token';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  AiTokenModelUsage,
  AiTokenPricing,
  AiTokenPricingUpdate,
  TopUpCatalog,
  TopUpPack,
  TopUpPackCreate,
  TopUpPackUpdate,
} from '../../modules/goi-nap-token/types';

/**
 * Bán token Fox (ADR-0100, tenant-service): gói nạp token (tag `resource-packages`), danh mục cho Tenant Admin
 * (`ai-token-grants`), giá bán và bảng chia theo model (`ai-quota`).
 *
 * - Gói nạp token: đọc `PLATFORM_POLICY.READ` hoặc `TENANT.READ`, ghi `PLATFORM_POLICY.UPDATE`.
 * - Giá bán: `AI_PRICING.READ` / `AI_PRICING.UPDATE`.
 * - Danh mục đang bán và bảng theo model của một tenant: Platform Admin hoặc Tenant Admin của tenant đó.
 *
 * Mỗi hàm chọn lúc gọi giữa API thật và máy chủ giả (`mock/goi-nap-token.ts`).
 */
const GOI = '/api/v1/security/top-up-packs';
const GIA = '/api/v1/security/ai-token-pricing';
const tenant = (id: string) => `/api/v1/tenants/${encodeURIComponent(id)}`;

const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

export const goiNapTokenRepository = {
  /** `GET /api/v1/security/top-up-packs` — mọi gói, đang bán trước. */
  danhSach: (): Promise<TopUpPack[]> =>
    dungApiThat()
      ? goiApi<{ items: TopUpPack[] }>(GOI).then((t) => t.items)
      : mockRequest<TopUpPack[] | null>(null, []).then((r) => r ?? mayChuGoiNapToken.danhSach()),

  /** `POST` — `code` bỏ trống là server sinh từ `name`. */
  tao: (du: TopUpPackCreate, khoaLap: string): Promise<TopUpPack> =>
    dungApiThat()
      ? goiApi<TopUpPack>(GOI, { method: 'POST', body: du, idempotencyKey: khoaLap })
      : giaLap(() => mayChuGoiNapToken.tao(du)),

  /** `PATCH …/{code}` — `price_usd: null` là bỏ giá ghi đè. */
  sua: (code: string, du: TopUpPackUpdate): Promise<TopUpPack> =>
    dungApiThat()
      ? goiApi<TopUpPack>(`${GOI}/${encodeURIComponent(code)}`, { method: 'PATCH', body: du })
      : giaLap(() => mayChuGoiNapToken.sua(code, du)),

  /** `GET /api/v1/tenants/{id}/top-up-packs` — gói đang bán kèm giá, đơn giá, mức nạp gợi ý. */
  dangBan: (tenantId: string): Promise<TopUpCatalog> =>
    dungApiThat()
      ? goiApi<TopUpCatalog>(`${tenant(tenantId)}/top-up-packs`)
      : giaLap(() => {
          const c = mayChuGoiNapToken.catalog();
          return mockState() === 'empty' ? { ...c, items: [] } : c;
        }),

  /** `GET /api/v1/security/ai-token-pricing`. */
  giaBan: (): Promise<AiTokenPricing> =>
    dungApiThat() ? goiApi<AiTokenPricing>(GIA) : giaLap(() => mayChuGoiNapToken.giaBan()),

  /** `PUT /api/v1/security/ai-token-pricing` — thay cả hai trường. */
  datGiaBan: (du: AiTokenPricingUpdate): Promise<AiTokenPricing> =>
    dungApiThat()
      ? goiApi<AiTokenPricing>(GIA, { method: 'PUT', body: du })
      : giaLap(() => mayChuGoiNapToken.datGiaBan(du)),

  /** `GET /api/v1/tenants/{id}/ai-token-quota/models?month=yyyy-MM` — bỏ trống là tháng này. */
  theoModel: (tenantId: string, thang?: string): Promise<AiTokenModelUsage> =>
    dungApiThat()
      ? goiApi<AiTokenModelUsage>(`${tenant(tenantId)}/ai-token-quota/models`, { query: { month: thang } })
      : giaLap(() =>
          mockState() === 'empty'
            ? { tenant_id: tenantId, month: `${thang ?? new Date().toISOString().slice(0, 7)}-01`, raw_tokens: 0, fox_tokens: 0, items: [] }
            : mayChuGoiNapToken.theoModel(tenantId, thang, mayChuHanMucToken.daDungThang(tenantId)),
        ),
};
